import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tokens, setTokens] = useState(() => {
    const stored = localStorage.getItem('acodia_tokens');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  const saveTokens = useCallback((t) => {
    setTokens(t);
    if (t) {
      localStorage.setItem('acodia_tokens', JSON.stringify(t));
    } else {
      localStorage.removeItem('acodia_tokens');
    }
  }, []);

  const authFetch = useCallback(async (url, options = {}) => {
    const t = tokens || JSON.parse(localStorage.getItem('acodia_tokens') || 'null');
    if (!t) throw new Error('Not authenticated');
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${t.access_token}`,
    };
    let res = await fetch(url, { ...options, headers });
    if (res.status === 401 && t.refresh_token) {
      const refreshRes = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${t.refresh_token}` },
      });
      if (refreshRes.ok) {
        const newTokens = await refreshRes.json();
        saveTokens(newTokens);
        headers['Authorization'] = `Bearer ${newTokens.access_token}`;
        res = await fetch(url, { ...options, headers });
      } else {
        saveTokens(null);
        setUser(null);
        throw new Error('Session expired');
      }
    }
    return res;
  }, [tokens, saveTokens]);

  const fetchUser = useCallback(async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/auth/me`);
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        setUser(null);
        saveTokens(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [authFetch, saveTokens]);

  useEffect(() => {
    if (tokens?.access_token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, totpCode) => {
    const body = { email, password, honeypot: '' };
    if (totpCode) body.totp_code = totpCode;
    const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    if (data.requires_totp) return { requires_totp: true };
    saveTokens(data);
    await fetchUser();
    return { success: true };
  };

  const register = async (email, password, confirmPassword) => {
    const res = await fetch(`${BACKEND_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, confirm_password: confirmPassword, honeypot: '' }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Registration failed');
    return data;
  };

  const logout = async () => {
    try {
      await authFetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST' });
    } catch {}
    saveTokens(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, tokens, loading, login, register, logout, authFetch, fetchUser, saveTokens, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
